import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatCareLabel } from '../../care/labels';
import './CarePanels.css';

function parseList(value) {
  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function getCareCopy(language) {
  const isFrench = language === 'fr';
  return {
    save: isFrench ? 'Enregistrer' : 'Save changes',
    decideLater: isFrench ? 'Decider plus tard' : 'Decide later',
    open: isFrench ? 'Ouvrir' : 'Open',
    noRecommendations: isFrench
      ? 'Aucune relance active pour le moment. Les suggestions apparaitront a mesure que les donnees et observations seront ajoutees.'
      : 'No active follow-up suggestions yet. New recommendations will appear as profile details and observations are added.',
    subjectTitle: isFrench ? 'Contexte de soins' : 'Care context',
    subjectBody: isFrench
      ? 'Basculez entre vos propres soins et les beneficiaires que vous etes autorise a aider.'
      : 'Switch between your own care plan and any beneficiary you are authorized to support.',
    myCare: isFrench ? 'Mes soins' : 'My care',
    delegateAccess: isFrench ? 'Acces delegue' : 'Delegate access',
    nextStepsBody: isFrench
      ? 'Des suggestions claires et tracables basees sur le profil, les suivis et les observations documentees.'
      : 'Clear, explainable suggestions based on profile completion, follow-up needs, and documented observations.',
    whyRecommended: isFrench ? 'Pourquoi cette recommandation' : 'Why this is recommended',
    safetyNote: isFrench ? 'Note de securite' : 'Safety note',
    noSubject: isFrench ? 'Aucun dossier de soins actif.' : 'No active care profile selected.',
    profileTitle: isFrench ? 'Profil de sante' : 'Health profile',
    profileBody: isFrench
      ? 'Un profil progressif, pret pour les suivis IA, les appareils connectes et la coordination multilingue.'
      : 'A guided health profile designed for future AI follow-up, devices, and multilingual care coordination.',
    profileComplete: isFrench ? 'complet' : 'complete',
    saveProfile: isFrench ? 'Mettre a jour le profil' : 'Update profile',
    commaHint: isFrench ? 'Separez les elements avec des virgules' : 'Separate items with commas',
    consentTitle: isFrench ? 'Consentement et acces delegue' : 'Consent and delegated access',
    consentBody: isFrench
      ? 'Gardez les autorisations explicites, revocables et faciles a revoir.'
      : 'Keep permissions explicit, reviewable, and easy to revoke before someone helps manage care.',
    grantAccess: isFrench ? 'Ajouter un contact de confiance' : 'Add a trusted contact',
    noConsent: isFrench ? 'Aucun acces delegue configure pour le moment.' : 'No delegated access has been set up yet.',
    revokeAccess: isFrench ? 'Retirer l acces' : 'Remove access',
    observationTitle: isFrench ? 'Observations structurees et notes intelligentes' : 'Structured observations and smart notes',
    observationBody: isFrench
      ? 'Combinez des champs structures et du contexte libre pour alimenter des suivis fiables.'
      : 'Capture structured fields and a plain-language note so follow-up suggestions stay traceable and useful.',
    saveObservation: isFrench ? 'Enregistrer l observation' : 'Save observation',
    timelineTitle: isFrench ? 'Historique et audit' : 'Activity and audit trail',
    timelineBody: isFrench
      ? 'Chaque recommandation et changement d acces doit rester relie a une observation ou a une action utilisateur.'
      : 'Each recommendation and access change should stay connected to a real observation or user action.',
    noTimeline: isFrench ? 'Aucune activite enregistree pour le moment.' : 'No activity has been recorded yet.',
    feedbackTitle: isFrench ? 'Commentaires et bilan' : 'Feedback and reflection',
    feedbackBody: isFrench
      ? 'Recueillez des retours reutilisables apres un service ou un parcours de suivi.'
      : 'Capture reusable reflections after a service or follow-up journey.',
    submitFeedback: isFrench ? 'Envoyer les commentaires' : 'Submit feedback',
    placeholders: {
      delegateName: isFrench ? 'Nom complet' : 'Full name',
      delegateEmail: isFrench ? 'name@example.com' : 'name@example.com',
      issue: isFrench ? 'Ex. coupure au pied' : 'For example: foot cut',
      symptom: isFrench ? 'Ex. cut, cough, mobility' : 'For example: cut, cough, mobility',
      followUp: isFrench ? 'Ex. suivi en soins des pieds' : 'For example: foot care follow-up',
      smartNote: isFrench
        ? 'Decrivez l observation, le contexte, les attentes ou les inquietudes.'
        : 'Describe the observation, context, expectations, or concerns.',
      reflection: isFrench ? 'Partagez votre experience.' : 'Share the experience in a few words.',
      outcome: isFrench ? 'Qu est-ce qui s est bien passe ou pourrait etre ameliore ?' : 'What went well, or what should improve next time?'
    }
  };
}

function getHealthProfileFields(language) {
  const isFrench = language === 'fr';
  return [
    { key: 'dateOfBirth', label: isFrench ? 'Date de naissance' : 'Date of birth', type: 'date', placeholder: '' },
    { key: 'phone', label: isFrench ? 'Telephone' : 'Phone', type: 'text', placeholder: isFrench ? 'Numero principal' : 'Main phone number' },
    { key: 'address', label: isFrench ? 'Adresse' : 'Address', type: 'text', placeholder: isFrench ? 'Adresse complete' : 'Full address' },
    { key: 'emergencyContactName', label: isFrench ? 'Contact d urgence' : 'Emergency contact', type: 'text', placeholder: isFrench ? 'Nom du contact' : 'Contact name' },
    { key: 'emergencyContactPhone', label: isFrench ? 'Telephone d urgence' : 'Emergency phone', type: 'text', placeholder: isFrench ? 'Telephone du contact' : 'Contact phone' },
    { key: 'preferredLanguage', label: isFrench ? 'Langue preferee' : 'Preferred language', type: 'text', placeholder: isFrench ? 'Ex. anglais' : 'For example: English' },
    { key: 'mobilityStatus', label: isFrench ? 'Mobilite et autonomie' : 'Mobility and function', type: 'text', placeholder: isFrench ? 'Decrivez les besoins de mobilite' : 'Describe any mobility needs' },
    { key: 'carePreferences', label: isFrench ? 'Preferences de soins' : 'Care preferences', type: 'textarea', placeholder: isFrench ? 'Preferences de soins, horaires ou confort' : 'Care preferences, scheduling, or comfort notes' },
    { key: 'behaviorObservations', label: isFrench ? 'Observations comportementales' : 'Behaviour observations', type: 'textarea', placeholder: isFrench ? 'Notes utiles pour le suivi' : 'Helpful notes for ongoing care' },
    { key: 'cognitiveNotes', label: isFrench ? 'Notes cognitives' : 'Cognitive notes', type: 'textarea', placeholder: isFrench ? 'Memoire, comprehension, orientation' : 'Memory, understanding, orientation' },
    { key: 'devicePlaceholders', label: isFrench ? 'Appareils et donnees connectees' : 'Devices and connected data', type: 'textarea', placeholder: isFrench ? 'Appareils actuels ou donnees a connecter plus tard' : 'Current devices or future wearable data' }
  ];
}

export function SubjectSwitcher({ managedSubjects = [], activeSubjectId, onChange }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  if (managedSubjects.length <= 1) return null;

  return (
    <div className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{copy.subjectTitle}</h3>
          <p>{copy.subjectBody}</p>
        </div>
      </div>
      <div className="care-subject-switcher">
        {managedSubjects.map((subject) => (
          <button
            key={subject.id}
            className="care-action"
            type="button"
            onClick={() => onChange(subject.id)}
            style={subject.id === activeSubjectId ? { boxShadow: '0 0 0 3px rgba(56, 189, 248, 0.25)' } : undefined}
          >
            <strong>{subject.displayName}</strong>
            <span>{subject.relationshipLabel === 'self' ? copy.myCare : `${copy.delegateAccess}: ${formatCareLabel(subject.relationshipLabel, language)}`}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function RecommendationPanel({ title = 'Next best actions', recommendations = [], onAction }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  return (
    <section className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{title}</h3>
          <p>{copy.nextStepsBody}</p>
        </div>
      </div>
      {recommendations.length ? (
        <div className="care-grid">
          {recommendations.map((recommendation) => (
            <article className="care-task" key={recommendation.id}>
              <div className="care-task__header">
                <div>
                  <h4>{recommendation.title}</h4>
                  <p>{recommendation.body}</p>
                </div>
                <span className={`care-task__priority care-task__priority--${recommendation.priority || 'low'}`}>
                  {formatCareLabel(recommendation.priority || 'low', language)}
                </span>
              </div>
              {recommendation.reason ? <p><strong>{copy.whyRecommended}:</strong> {recommendation.reason}</p> : null}
              {recommendation.safeNote ? <p><strong>{copy.safetyNote}:</strong> {recommendation.safeNote}</p> : null}
              <div className="care-form__actions">
                <button className="care-form__primary" type="button" onClick={() => onAction(recommendation, 'accept')}>
                  {recommendation.actionLabel || copy.open}
                </button>
                <button className="care-form__secondary" type="button" onClick={() => onAction(recommendation, 'defer')}>
                  {copy.decideLater}
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="care-empty">{copy.noRecommendations}</div>
      )}
    </section>
  );
}

export function HealthProfilePanel({ subject, completeness, onSave }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  const healthProfileFields = useMemo(() => getHealthProfileFields(language), [language]);
  const [form, setForm] = useState(() => {
    const profile = subject?.healthProfile || {};
    return {
      ...profile,
      conditions: (profile.conditions || []).join(', '),
      medications: (profile.medications || []).join(', '),
      allergies: (profile.allergies || []).join(', '),
      symptoms: (profile.symptoms || []).join(', '),
      diagnoses: (profile.diagnoses || []).join(', '),
      riskFactors: (profile.riskFactors || []).join(', '),
      delegationPermissions: (profile.delegationPermissions || []).join(', ')
    };
  });

  React.useEffect(() => {
    const profile = subject?.healthProfile || {};
    setForm({
      ...profile,
      conditions: (profile.conditions || []).join(', '),
      medications: (profile.medications || []).join(', '),
      allergies: (profile.allergies || []).join(', '),
      symptoms: (profile.symptoms || []).join(', '),
      diagnoses: (profile.diagnoses || []).join(', '),
      riskFactors: (profile.riskFactors || []).join(', '),
      delegationPermissions: (profile.delegationPermissions || []).join(', ')
    });
  }, [subject]);

  if (!subject) return <div className="care-empty">{copy.noSubject}</div>;

  return (
    <section className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{copy.profileTitle}</h3>
          <p>{copy.profileBody}</p>
        </div>
        <div className="care-progress-block">
          <strong>{completeness.percentage}% {copy.profileComplete}</strong>
          <div className="care-progress" aria-hidden="true">
            <div className="care-progress__bar" style={{ width: `${completeness.percentage}%` }} />
          </div>
        </div>
      </div>

      <form
        className="care-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            ...form,
            conditions: parseList(form.conditions),
            medications: parseList(form.medications),
            allergies: parseList(form.allergies),
            symptoms: parseList(form.symptoms),
            diagnoses: parseList(form.diagnoses),
            riskFactors: parseList(form.riskFactors),
            delegationPermissions: parseList(form.delegationPermissions)
          });
        }}
      >
        <div className="care-form__section">
          <h4>{language === 'fr' ? 'Coordonnees et urgence' : 'Contact and emergency details'}</h4>
          <div className="care-form__grid">
          {healthProfileFields.slice(0, 6).map((field) => (
            <label key={field.key}>
              <span>{field.label}</span>
              {field.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={form[field.key] || ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              ) : (
                <input
                  type={field.type}
                  value={form[field.key] || ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              )}
            </label>
          ))}
          </div>
        </div>
        <div className="care-form__section">
          <h4>{language === 'fr' ? 'Profil clinique et preferences' : 'Clinical profile and preferences'}</h4>
          <div className="care-form__grid">
          {healthProfileFields.slice(6).map((field) => (
            <label key={field.key}>
              <span>{field.label}</span>
              {field.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={form[field.key] || ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              ) : (
                <input
                  type={field.type}
                  value={form[field.key] || ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              )}
            </label>
          ))}
          {['conditions', 'medications', 'allergies', 'symptoms', 'diagnoses', 'riskFactors'].map((field) => (
            <label key={field}>
              <span>{formatCareLabel(field, language)}</span>
              <input
                type="text"
                value={form[field] || ''}
                onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))}
                placeholder={copy.commaHint}
              />
            </label>
          ))}
          </div>
        </div>
        <div className="care-form__actions">
          <button className="care-form__primary" type="submit">{copy.saveProfile}</button>
        </div>
      </form>
    </section>
  );
}

export function ConsentPanel({ consents = [], onCreate, onRevoke }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  const [form, setForm] = useState({
    delegateName: '',
    delegateEmail: '',
    relationship: 'family member',
    permissions: ['booking', 'follow_up']
  });

  return (
    <section className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{copy.consentTitle}</h3>
          <p>{copy.consentBody}</p>
        </div>
      </div>
      <form
        className="care-form"
        onSubmit={(event) => {
          event.preventDefault();
          onCreate(form);
          setForm({
            delegateName: '',
            delegateEmail: '',
            relationship: 'family member',
            permissions: ['booking', 'follow_up']
          });
        }}
      >
        <div className="care-form__section">
          <h4>{language === 'fr' ? 'Contact de confiance' : 'Trusted contact'}</h4>
          <div className="care-form__grid">
          <label>
            <span>{language === 'fr' ? 'Nom' : 'Name'}</span>
            <input placeholder={copy.placeholders.delegateName} value={form.delegateName} onChange={(event) => setForm((current) => ({ ...current, delegateName: event.target.value }))} />
          </label>
          <label>
            <span>{language === 'fr' ? 'Adresse courriel' : 'Email address'}</span>
            <input type="email" placeholder={copy.placeholders.delegateEmail} value={form.delegateEmail} onChange={(event) => setForm((current) => ({ ...current, delegateEmail: event.target.value }))} />
          </label>
          <label>
            <span>{language === 'fr' ? 'Lien avec la personne' : 'Relationship'}</span>
            <select value={form.relationship} onChange={(event) => setForm((current) => ({ ...current, relationship: event.target.value }))}>
              <option value="family member">{formatCareLabel('family_member', language)}</option>
              <option value="caregiver">{formatCareLabel('caregiver', language)}</option>
              <option value="parent">{formatCareLabel('parent', language)}</option>
              <option value="guardian">{formatCareLabel('guardian', language)}</option>
            </select>
          </label>
          <fieldset>
            <span>{language === 'fr' ? 'Autorisations' : 'Permissions'}</span>
            <div className="care-form__chips">
              {['booking', 'follow_up', 'profile_updates', 'reminders'].map((permission) => {
                const active = form.permissions.includes(permission);
                return (
                  <button
                    key={permission}
                    type="button"
                    className={`care-form__chip ${active ? 'is-active' : ''}`}
                    onClick={() => setForm((current) => ({
                      ...current,
                      permissions: active
                        ? current.permissions.filter((entry) => entry !== permission)
                        : [...current.permissions, permission]
                    }))}
                  >
                    {formatCareLabel(permission, language)}
                  </button>
                );
              })}
            </div>
          </fieldset>
          </div>
        </div>
        <div className="care-form__actions">
          <button className="care-form__primary" type="submit">{copy.grantAccess}</button>
        </div>
      </form>

      <div className="care-grid" style={{ marginTop: '1rem' }}>
        {consents.length ? consents.map((consent) => (
          <article className="care-card" key={consent.id}>
            <h4 style={{ marginTop: 0 }}>{consent.delegateName || consent.delegateEmail}</h4>
            <p>{formatCareLabel(consent.relationship, language)} • {formatCareLabel(consent.status, language)}</p>
            <div className="care-pill-row">
              {(consent.permissions || []).map((permission) => (
                <span className="care-pill" key={permission}>{formatCareLabel(permission, language)}</span>
              ))}
            </div>
            {consent.status === 'active' ? (
              <div className="care-form__actions">
                <button className="care-form__secondary" type="button" onClick={() => onRevoke(consent.id)}>
                  {copy.revokeAccess}
                </button>
              </div>
            ) : null}
          </article>
        )) : <div className="care-empty">{copy.noConsent}</div>}
      </div>
    </section>
  );
}

export function ObservationPanel({ onSubmit, subjects = [], activeSubjectId }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  const [form, setForm] = useState({
    subjectId: activeSubjectId || '',
    noteType: 'provider_observation',
    category: 'symptom',
    issue: '',
    symptom: '',
    severity: 'medium',
    freeText: '',
    recommendedFollowUp: '',
    expectedReviewDate: ''
  });

  React.useEffect(() => {
    setForm((current) => ({ ...current, subjectId: activeSubjectId || current.subjectId }));
  }, [activeSubjectId]);

  return (
    <section className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{copy.observationTitle}</h3>
          <p>{copy.observationBody}</p>
        </div>
      </div>
      <form
        className="care-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
          setForm((current) => ({
            ...current,
            issue: '',
            symptom: '',
            freeText: '',
            recommendedFollowUp: '',
            expectedReviewDate: ''
          }));
        }}
      >
        <div className="care-form__grid">
          <label>
            <span>{language === 'fr' ? 'Beneficiaire' : 'Beneficiary'}</span>
            <select value={form.subjectId} onChange={(event) => setForm((current) => ({ ...current, subjectId: event.target.value }))}>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>{subject.displayName}</option>
              ))}
            </select>
          </label>
          <label>
            <span>{language === 'fr' ? 'Type de note' : 'Note type'}</span>
            <select value={form.noteType} onChange={(event) => setForm((current) => ({ ...current, noteType: event.target.value }))}>
              <option value="provider_observation">{formatCareLabel('provider_observation', language)}</option>
              <option value="caregiver_observation">{formatCareLabel('caregiver_observation', language)}</option>
              <option value="symptom_log">{formatCareLabel('symptom_log', language)}</option>
              <option value="milestone">{formatCareLabel('milestone', language)}</option>
            </select>
          </label>
          <label>
            <span>{language === 'fr' ? 'Categorie' : 'Category'}</span>
            <select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}>
              <option value="symptom">{formatCareLabel('symptom', language)}</option>
              <option value="mobility">{formatCareLabel('mobility', language)}</option>
              <option value="behavior">{formatCareLabel('behavior', language)}</option>
              <option value="wound">{formatCareLabel('wound', language)}</option>
              <option value="follow_up">{formatCareLabel('follow_up', language)}</option>
            </select>
          </label>
          <label>
            <span>{language === 'fr' ? 'Priorite' : 'Priority'}</span>
            <select value={form.severity} onChange={(event) => setForm((current) => ({ ...current, severity: event.target.value }))}>
              <option value="low">{formatCareLabel('low', language)}</option>
              <option value="medium">{formatCareLabel('medium', language)}</option>
              <option value="high">{formatCareLabel('high', language)}</option>
            </select>
          </label>
          <label>
            <span>{language === 'fr' ? 'Probleme observe' : 'Observed issue'}</span>
            <input placeholder={copy.placeholders.issue} value={form.issue} onChange={(event) => setForm((current) => ({ ...current, issue: event.target.value }))} />
          </label>
          <label>
            <span>{language === 'fr' ? 'Mot-cle ou symptome' : 'Symptom or keyword'}</span>
            <input placeholder={copy.placeholders.symptom} value={form.symptom} onChange={(event) => setForm((current) => ({ ...current, symptom: event.target.value }))} />
          </label>
          <label>
            <span>{language === 'fr' ? 'Date de revision souhaitee' : 'Suggested review date'}</span>
            <input type="date" value={form.expectedReviewDate} onChange={(event) => setForm((current) => ({ ...current, expectedReviewDate: event.target.value }))} />
          </label>
          <label>
            <span>{language === 'fr' ? 'Suivi recommande' : 'Suggested next step'}</span>
            <input placeholder={copy.placeholders.followUp} value={form.recommendedFollowUp} onChange={(event) => setForm((current) => ({ ...current, recommendedFollowUp: event.target.value }))} />
          </label>
        </div>
        <label>
          <span>{language === 'fr' ? 'Note contextuelle' : 'Care note'}</span>
          <textarea rows={4} placeholder={copy.placeholders.smartNote} value={form.freeText} onChange={(event) => setForm((current) => ({ ...current, freeText: event.target.value }))} />
        </label>
        <div className="care-form__actions">
          <button className="care-form__primary" type="submit">{copy.saveObservation}</button>
        </div>
      </form>
    </section>
  );
}

export function TimelinePanel({ observations = [], auditLog = [] }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  const items = useMemo(
    () => [...observations.map((entry) => ({ ...entry, kind: 'observation', at: entry.createdAt })), ...auditLog.map((entry) => ({ ...entry, kind: 'audit', at: entry.at }))]
      .sort((a, b) => new Date(b.at) - new Date(a.at))
      .slice(0, 10),
    [auditLog, observations]
  );

  return (
    <section className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{copy.timelineTitle}</h3>
          <p>{copy.timelineBody}</p>
        </div>
      </div>
      <div className="care-timeline">
        {items.length ? items.map((item) => (
          <article className="care-timeline__item" key={item.id}>
            <strong>{item.kind === 'audit' ? item.action : item.issue || item.noteType}</strong>
            <p>{item.detail || item.freeText || item.symptom || item.category || 'Structured care event'}</p>
            <div className="care-timeline__meta">{new Date(item.at).toLocaleString()}</div>
          </article>
        )) : <div className="care-empty">{copy.noTimeline}</div>}
      </div>
    </section>
  );
}

export function FeedbackPanel({ onSubmit }) {
  const { language } = useLanguage();
  const copy = getCareCopy(language);
  const [form, setForm] = useState({
    audience: 'patient_family',
    rating: '5',
    reflection: '',
    outcomeNote: ''
  });

  return (
    <section className="care-panel">
      <div className="care-panel__header">
        <div>
          <h3>{copy.feedbackTitle}</h3>
          <p>{copy.feedbackBody}</p>
        </div>
      </div>
      <form
        className="care-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
          setForm({
            audience: 'patient_family',
            rating: '5',
            reflection: '',
            outcomeNote: ''
          });
        }}
      >
        <div className="care-form__grid">
          <label>
            <span>{language === 'fr' ? 'Type de retour' : 'Feedback type'}</span>
            <select value={form.audience} onChange={(event) => setForm((current) => ({ ...current, audience: event.target.value }))}>
              <option value="patient_family">{formatCareLabel('patient_family', language)}</option>
              <option value="provider_platform">{formatCareLabel('provider_platform', language)}</option>
              <option value="care_outcome">{formatCareLabel('care_outcome', language)}</option>
            </select>
          </label>
          <label>
            <span>{language === 'fr' ? 'Evaluation' : 'Rating'}</span>
            <select value={form.rating} onChange={(event) => setForm((current) => ({ ...current, rating: event.target.value }))}>
              <option value="5">5</option>
              <option value="4">4</option>
              <option value="3">3</option>
              <option value="2">2</option>
              <option value="1">1</option>
            </select>
          </label>
        </div>
        <label>
          <span>{language === 'fr' ? 'Commentaire' : 'Reflection'}</span>
          <textarea rows={3} placeholder={copy.placeholders.reflection} value={form.reflection} onChange={(event) => setForm((current) => ({ ...current, reflection: event.target.value }))} />
        </label>
        <label>
          <span>{language === 'fr' ? 'Bilan ou resultat' : 'Outcome note'}</span>
          <textarea rows={3} placeholder={copy.placeholders.outcome} value={form.outcomeNote} onChange={(event) => setForm((current) => ({ ...current, outcomeNote: event.target.value }))} />
        </label>
        <div className="care-form__actions">
          <button className="care-form__primary" type="submit">{copy.submitFeedback}</button>
        </div>
      </form>
    </section>
  );
}
